import api from "../utils/api";
import { S_AUTH_TOKEN, S_COMMENTS_COUNTRIES, S_COUNTRY_LIST, S_DICTIONARY, S_GENDER, S_ID_NUMBER_RULE_LIST, S_LANG, S_MARKET_LIST, S_SITE_COUNTRY_LIST, webStorage } from "../utils/webStorage";
import { BF } from "../utils/dictionary";
import { gQuery } from "../Graphql/gQuery";
import queryApiErrorAll from "../Graphql/queryApiErrorAll";
import getSiteInfo from "../utils/getSiteInfo";
import queryRegions from "../Graphql/queryRegions";
import countriesQuery from "../Graphql/queryCountries";
import queryDropdownDefaultOption from "../Graphql/queryDropdown";
import { isEmptyObject, isGlobalSite } from "../utils/siteUtils";
import "./siteService.jsdoc";
import queryIdNumberRules from "../Graphql/queryIdNumberRules";
import queryGender from "../Graphql/queryGender";
import config from "../temp/config";
import { apiAsia } from "../utils/api-asia";

export const getEncryptor = async email => {
  const {
    obj: { rsaKey }
  } = await api.auth.publicKey(null, {
    email: email
  });
  const JSEncrypt = (await import("jsencrypt")).default;
  const encryptor = new JSEncrypt();
  encryptor.setPublicKey(rsaKey);
  return encryptor;
};
export const getMarketList = async () => {
  const cache = webStorage.get(S_MARKET_LIST) || {};
  const lang = webStorage.get(S_LANG);
  if (cache[lang]) {
    return cache[lang];
  }
  const res = await gQuery({
    query: queryRegions,
    variables: {
      language: lang
    }
  });
  const {
    data: { obj }
  } = res;
  const { list = [] } = obj || {};
  const countries = await getCountryList();
  const result = list.reduce((prev, current) => {
    const { country, language } = current;
    if (country.fields && language.count > 0) {
      const c = countries.find(x => x.regionAbbr === country.fields.phrase.value);
      language.list.forEach(l => {
        prev.push({
          code: c?.regionId.toString() || "",
          value: `${c?.regionName} - ${l.key.value}`,
          language: `${l.phrase.value}-${country.fields.phrase.value}`
        });
      });
    }
    return prev;
  }, []);
  cache[lang] = result;
  webStorage.set(S_MARKET_LIST, cache);
  return result;
};
export const getSiteCountryList = async (lang = "") => {
  if (!lang) {
    lang = webStorage.get(S_LANG);
  }
  const cache = webStorage.get(S_SITE_COUNTRY_LIST) || {};
  if (cache[lang] instanceof Array) {
    return cache[lang];
  } else {
    const query = countriesQuery(lang);
    const result = await api.graphql(null, { query: query });
    const { children = [] } = result.data.countries || {};
    const data = children.map(x => ({
      name: x.key.value,
      code: x.phrase.value
    }));
    cache[lang] = data;
    return data;
  }
};
export const getSiteCountry = async (lang = "") => {
  if (!lang) {
    lang = webStorage.get(S_LANG);
  }
  const countryCode = isGlobalSite() ? "" : lang.toLowerCase().split("-")[1];
  const countries = await getSiteCountryList(lang);
  return countries.find(x => x.code.toLowerCase() === countryCode);
};
export const getCountryList = async () => {
  const lang = webStorage.get(S_LANG);
  const cache = webStorage.get(S_COUNTRY_LIST) || {};
  if (cache[lang] instanceof Array) {
    return cache[lang];
  } else {
    const {
      obj: { regions }
    } = await api.country.list(null, { lang });
    cache[lang] = regions;
    webStorage.set(S_COUNTRY_LIST, cache);
    return regions;
  }
};
/**
 * @param {String} lang
 * @return {Promise<Country>}
 */
export const getMarketCountry = async (lang = "") => {
  if (!lang) {
    lang = webStorage.get(S_LANG);
  }
  const countries = await getCountryList();
  const siteInfo = await getSiteInfo(lang);
  return (
    countries.find(x => x.regionId == siteInfo.country) || {
      regionName: "Global"
    }
  );
};
/**
 * @param {String} lang
 * @return {String}
 */
export const getMarketCountryCode = (lang = "") => {
  if (!lang) {
    lang = webStorage.get(S_LANG);
  }
  const parts = lang.split("-");
  return parts.length > 1 ? parts[1].toUpperCase() : "";
};
export const isNetworkError = exception => {
  return exception.message === "Network Error";
};
/**
 * @param {String} key
 * @return {Promise<Array.<BFDictionaryEntry>>}
 */
export const getDictionary = async key => {
  const dict = webStorage.get(S_DICTIONARY(key));
  if (dict) {
    return dict;
  } else {
    const res = await BF.get([key]);
    webStorage.set(S_DICTIONARY(key), res[key]);
    return res[key];
  }
};
/**
 * @method getDictionaries
 * @param {Array.<String>} keys
 * @return {Promise<Array.<Array.<DictionaryEntry>>>}
 */
export const getDictionaries = async keys => {
  const len = keys.length;
  const result = [];
  const requestKeyObjects = [];
  const requestKeys = [];
  for (let i = 0; i < len; i++) {
    let key = keys[i];
    const dict = webStorage.get(S_DICTIONARY(key));
    if (dict) {
      result[i] = dict;
    } else {
      requestKeyObjects.push({
        index: i,
        key
      });
      requestKeys.push(key);
    }
  }
  if (requestKeys.length > 0) {
    const res = await BF.get(requestKeys);
    let n = requestKeys.length;
    for (let i = 0; i < n; i++) {
      let { index, key } = requestKeyObjects[i];
      webStorage.set(S_DICTIONARY(key), res[key]);
      result[index] = res[key];
    }
  }
  return result;
};
export const getMarketLanguage = () => {
  const lang = webStorage.get(S_LANG);
  return lang ? lang.split("-")[0].toUpperCase() : "EN";
};
export const getApiErrors = async () => {
  const lang = webStorage.get(S_LANG);
  // let cache = webStorage.get(S_API_ERROR) || {};
  const buildErrors = tempList => {
    tempList.forEach(it => {
      const icon = it.iconName;
      const link = it.link;
      it.iconName = { value: icon };
      it.link.value = { ...link };
    });
  };
  // if (cache[lang]) {
  //   return cache[lang];
  // } else {
  const res = await gQuery({
    query: queryApiErrorAll,
    variables: {
      language: lang
    }
  });
  let list = res.data?.obj?.list;
  if (list) {
    // cache[lang] = list;
    // webStorage.set(S_API_ERROR, cache);
    buildErrors(res.data.obj.list);
    return list;
  } else return [];
  // }
};
export const getApiError = async code => {
  const errors = await getApiErrors();
  let error = errors.find(x => x.code.value === code);
  if (!error) {
    error = errors.find(x => x.code.value === "DEFAULT");
  }
  return error;
};
export const getCountryOptions = async () => {
  const countries = await getCountryList();
  return countries.map(e => ({
    value: e.regionName,
    code: e.regionId.toString(),
    data: e
  }));
};
export const getCountryByCode = async code => {
  const countries = await getCountryList();
  return countries.find(x => x.regionAbbr === code);
};
export const isLoggedIn = () => {
  const token = webStorage.get(S_AUTH_TOKEN);
  return !!token?.userAccount;
};
export const getIdNumberRuleList = async () => {
  // const cache = webStorage.get(S_ID_NUMBER_RULE_LIST);
  // if (!isEmptyObject(cache)) {
  //   return cache;
  // }
  const lang = webStorage.get(S_LANG);
  const { data } = await gQuery({
    query: queryIdNumberRules,
    variables: {
      language: lang
    }
  });
  if (isEmptyObject(data?.obj?.list)) {
    return [];
  }
  const {
    obj: { list }
  } = data;
  const result = list.map(x => ({
    countryCode: x.country.fields.phrase.value,
    country: x.country.fields.key.value,
    label: x.label.value,
    pattern: x.pattern.value,
    birthdayPattern: x.birthdayPattern.value
  }));
  webStorage.set(S_ID_NUMBER_RULE_LIST, result);
  console.log("result", result);
  return result;
};
export const getIdNumberRule = async (lang = "") => {
  const code = getMarketCountryCode(lang);
  const rules = await getIdNumberRuleList();
  return rules.find(x => x.countryCode === code);
};

export const getDropdownDefault = async () => {
  const lang = webStorage.get(S_LANG);

  const { data } = await gQuery({
    query: queryDropdownDefaultOption,
    variables: {
      language: lang
    }
  });
  if (isEmptyObject(data?.dropdown?.fields)) {
    return [];
  }
  return data?.dropdown?.fields;
};

export const getDropdownDefaultOption = async () => {
  const res = await getDropdownDefault();
  return res?.find(x => x.name === "dropdownDefaultOptionText")?.value;
};
export const getGenderOptions = async () => {
  const lang = webStorage.get(S_LANG);
  const cache = webStorage.get(S_GENDER) ?? {};
  if (isEmptyObject(cache[lang])) {
    const [data, ex] = await apiAsia.sitecore.item({
      id: config.webSettings.items.gender,
      lang
    });
    if (isEmptyObject(data?.list)) {
      return [];
    }
    cache[lang] = data?.list.map(x => ({
      value: x.text,
      code: x.phrase
    }));
    webStorage.set(S_GENDER, cache);
  }

  return cache[lang];
};

export const getCommentsCountryOptions = async () => {
  const lang = webStorage.get(S_LANG);
  const cache = webStorage.get(S_COMMENTS_COUNTRIES) ?? {};
  if (isEmptyObject(cache[lang])) {
    const [data, ex] = await apiAsia.sitecore.item({
      id: config.webSettings.items.commentsCountries,
      lang
    });
    if (isEmptyObject(data?.list)) {
      return [];
    }
    cache[lang] = data?.list.map(x => ({
      value: x.label,
      code: x.code,
      cities: x.list.map(y => ({
        value: y.label,
        code: y.code
      }))
    }));
    webStorage.set(S_COMMENTS_COUNTRIES, cache);
  }
  return cache[lang];
};
